import { Box, Button, Typography } from '@mui/material'
import { format, isToday, parseISO } from 'date-fns'
import { useEffect, useState } from 'react'
import TagManager from 'react-gtm-module'
import { localStorageItemKeys } from '~constants/local-storage-items'
import {
  GetWelcomePopupRes,
  useGetWelcomePopup,
} from '~hooks/queries/welcome-popup/use-get-welcome-popup'
import { useDateFormat } from '~hooks/use-date-format'
import { sanitizeRichText } from '~utils/sanitize-rich-text'
import { Image } from './sub-components/image'
import { DialogBox } from '~pages/pages-behind-login/chartering/pre-fixture-page/components/dialog-box'
import { COLORS } from '~theme/colors'
import { useGetFileUrl } from '~hooks/queries/files/use-get-file-url'
import { useGetMe } from '~hooks/queries/me/use-get-me'

export function WelcomePopup() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { data: welcomePopUpData } = useGetWelcomePopup()
  const { data: me } = useGetMe()

  const data = welcomePopUpData?.[0]

  const closedVersion = localStorage.getItem(
    localStorageItemKeys.closedWelcomePopupVersion,
  )

  useEffect(() => {
    if (!data || !me) return

    const isClosed = Number(closedVersion) >= data.version
    if (isClosed) return

    if (!data.isEnabled) return

    const isFirstLogin =
      !me.isInternal &&
      (!me.ToSAcceptedDate || isToday(parseISO(me.ToSAcceptedDate)))
    if (isFirstLogin) return

    setIsModalOpen(true)
    trackModalDisplayed()
  }, [data, me])

  function onCloseHandler() {
    setIsModalOpen(false)
    localStorage.setItem(
      localStorageItemKeys.closedWelcomePopupVersion,
      `${data?.version || 1}`,
    )
  }

  if (!data) {
    return null
  }

  if (closedVersion && Number(closedVersion) >= data.version) {
    return null
  }

  return (
    <WelcomePopupRenderer
      data={data}
      onClose={onCloseHandler}
      isModalOpen={isModalOpen}
    />
  )
}

interface WelcomePopupRendererData extends GetWelcomePopupRes {
  previewFile?: {
    signedUrl: string
    name: string
    subtype: string
  }
}

export function WelcomePopupRenderer({
  data,
  onClose,
  isModalOpen,
}: {
  data: WelcomePopupRendererData
  onClose: () => void
  isModalOpen: boolean
}) {
  const dateFormat = useDateFormat('literal')

  const { title, description, releaseDate, fileId, previewFile } = data

  const { refetch: fetchUploadedFile, data: uploadedFile } = useGetFileUrl({
    documentId: fileId ?? '',
  })

  useEffect(() => {
    if (fileId && !previewFile) {
      fetchUploadedFile()
    }
  }, [fileId])

  const processedDescription = description
    .replace(/<p><\/p>/g, '<br/>')
    .replace(/&nbsp;|&#160;/g, ' ')

  function handleDescriptionClick(e: React.MouseEvent<HTMLDivElement>) {
    const target = e.target as HTMLElement
    const anchor = target.closest('a')
    if (anchor instanceof HTMLAnchorElement && anchor.hasAttribute('href')) {
      e.preventDefault()
      const href = anchor.href
      onClose()
      setTimeout(() => {
        window.location.href = href
      }, 0) //  delay the redirect until after the modal has closed
    }
  }
  return (
    <DialogBox
      open={isModalOpen}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            minWidth: { xs: '300px', lg: '800px' },
          },
        },
      }}
    >
      <Typography variant="h3" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ mb: 4 }} component="p">
        {!releaseDate
          ? undefined
          : `Updated: ${format(releaseDate, dateFormat)}`}
      </Typography>
      <Box>
        {(() => {
          const file = previewFile || uploadedFile
          return file ? <MediaRenderer file={file} /> : null
        })()}
        <Typography
          component="article"
          variant="paragraph1"
          sx={{
            mt: previewFile || uploadedFile ? 3 : 0,
            mb: 4,
            u: { textDecoration: 'none' },
            a: {
              color: COLORS.primary.maerskBlue,
              textDecoration: 'none',
              span: {
                color: `${COLORS.primary.maerskBlue} !important`,
              },
            },
          }}
          dangerouslySetInnerHTML={{
            __html: sanitizeRichText(processedDescription),
          }}
          onClick={handleDescriptionClick}
        />
      </Box>
      <Button onClick={onClose} sx={{ float: 'right' }}>
        Close
      </Button>
    </DialogBox>
  )
}

function MediaRenderer({
  file,
}: {
  file: { subtype: string; signedUrl: string; name: string }
}) {
  return (
    <>
      {file.subtype === 'image' && (
        <Image src={file.signedUrl} alt={file.name || ''} />
      )}
      {file.subtype === 'video' && (
        <Box
          component="video"
          src={file.signedUrl}
          controls
          autoPlay
          sx={{ width: '100%', height: '100%' }}
        />
      )}
    </>
  )
}

function trackModalDisplayed() {
  const tagManagerArgs = {
    dataLayer: {
      event: 'welcome_modal_displayed',
    },
  }
  TagManager.dataLayer(tagManagerArgs)
}
